import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Billing, Service, ValidationError } from 'webapp-types';
import { validateEmail } from 'webapp/utils/validate';

import didUpdate from 'webapp/modifiers/did-update';
import BillingInvoiceRowComponent from 'webapp/components/billing/invoice-row';
import FormInputComponent from 'webapp/components/form/input';
import RowErrorComponent from 'webapp/components/row-error';
import SubmitButtonComponent from 'webapp/components/submit-button';

import { Severity } from 'webapp/services/logger';

interface Args {
	model: Billing.Invoice[];
}

export default class BillingInvoicesComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('billing') declare billingService: Service.Billing;
	@service('popup') declare popupService: Service.Popup;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('logger') declare loggerService: Service.Logger;

	@tracked billingEmail = '';
	@tracked isEditingBillingEmail = false;
	@tracked isSavingBillingEmail = false;

	@tracked billingEmailErrors: ValidationError[] = [];

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.updateEmailFromBillingService();
	}

	get isViewAllShown() {
		return this.args.model.length > 0;
	}

	@action
	updateEmailFromBillingService() {
		const email = this.billingService.billingEmail || '';
		if (typeof email !== 'string') {
			this.loggerService.logError('Invalid billingEmail', { email }, Severity.CRITICAL);
		}
		this.billingEmail = String(email);
	}

	@action
	viewAll() {
		this.billingService.openInvoicesPopup();
	}

	@action
	editBillingEmail() {
		this.isEditingBillingEmail = true;
	}

	@action
	cancelBillingEmailEditing() {
		this.updateEmailFromBillingService();
		this.isEditingBillingEmail = false;
	}

	@action
	validateBillingEmails() {
		const emails = this.billingEmail
			.split(',')
			.map((email) => email.trim())
			.filter((email) => email.length > 0);

		if (emails.length === 0) {
			this.billingEmailErrors = [{ message: 'At least one email is required' }];
			return false;
		}

		const invalidEmails: string[] = [];
		for (const email of emails) {
			if (!validateEmail({ email }, 'email')) {
				invalidEmails.push(email);
			}
		}

		if (invalidEmails.length > 0) {
			const errorMessage =
				invalidEmails.length === 1
					? `Invalid email: ${invalidEmails[0]}`
					: `Invalid emails: ${invalidEmails.join(', ')}`;
			this.billingEmailErrors = [{ message: errorMessage }];
			return false;
		}

		this.billingEmailErrors = [];
		return true;
	}

	@action
	saveBillingEmail() {
		if (this.isSavingBillingEmail) {
			return;
		}

		if (!this.validateBillingEmails()) {
			return;
		}

		this.isSavingBillingEmail = true;

		this.billingService
			.updateBillingEmail(this.billingEmail)
			.then(
				() => {
					this.infolayerService.ok('Invoicing email changed!');
					this.cancelBillingEmailEditing();
				},
				() => {
					this.infolayerService.error('Problem saving invoicing email!');
				},
			)
			.finally(() => {
				this.isSavingBillingEmail = false;
			});
	}
	<template>
		<div
			class="invoices"
			{{didUpdate this.updateEmailFromBillingService this.billingService.billingEmail}}
		>
			<div class="invoices__header">
				<div class="topline">
					<span>Recent invoice</span>
					{{#if this.isViewAllShown}}
						<a role="button" {{on "click" this.viewAll}}>View all</a>
					{{/if}}
				</div>
			</div>
			<div class="invoices__content">
				{{#if @model.length}}
					<div class="invoices__table">
						{{#each @model as |invoice|}}
							<BillingInvoiceRowComponent @model={{invoice}} />
						{{/each}}
					</div>
				{{/if}}

				<div class="invoice-address">
					<span>Invoices are sent to:</span>
					{{#if this.isEditingBillingEmail}}
						<div class="row edit">
							<FormInputComponent
								name="billing_email"
								disabled={{this.isSavingBillingEmail}}
								placeholder="Separate multiple emails with comma"
								@value={{this.billingEmail}}
								@focusOut={{this.validateBillingEmails}}
							/>
							<div class="actions">
								<SubmitButtonComponent
									@text="Save"
									@onTrigger={{this.saveBillingEmail}}
									@submitInProgress={{this.isSavingBillingEmail}}
								/>
								<a
									role="button"
									class="cancel"
									{{on "click" this.cancelBillingEmailEditing}}
								>Cancel
								</a>
							</div>
							<RowErrorComponent @items={{this.billingEmailErrors}} />
						</div>
					{{else}}
						<div class="invoice-address__emails">
							{{this.billingEmail}}
							<a role="button" {{on "click" this.editBillingEmail}}>
								Change
							</a>
						</div>
					{{/if}}
				</div>

			</div>

		</div>
	</template>
}
